import dayjs from 'dayjs';
import {useCallback} from 'react';
import {useSearchParams} from 'react-router-dom';
import {SocialStreamTab} from '@/types/socialMediaPost';
import campaignSocialMediaPostsFetchers from '@/api/fetchers/campaignSocialMediaPosts';
import {
  DATE_FILTER_UNSUPPORTED_SORT_FIELDS,
  normalizePostsSort,
  postsSortField,
} from '@/views/campaigns/stats/components/organic-posts-table/constants';
import {DateRange} from '@/components/date-range-picker/types';
import useCampaignMetricsQuery from '@/hooks/queries/use-campaign-metrics-query';
import useCampaignSocialMediaPostsQuery from '@/hooks/queries/use-campaign-social-media-posts-query';

interface UseOrganicContentControllerParams {
  campaignId: string;
  supportsStories?: boolean;
  supportsReels?: boolean;
  onTabChange?: (stream: SocialStreamTab) => void;
  socialStreamOverride?: SocialStreamTab;
  includeStreamCounts?: boolean;
  enabled?: boolean;
}

interface StreamCounts {
  all: number;
  feed: number;
  story: number;
  reels: number;
}

interface UseOrganicContentControllerResult {
  posts: ReturnType<typeof useCampaignSocialMediaPostsQuery>['posts'];
  campaignStats: ReturnType<typeof useCampaignMetricsQuery>['campaignStats'];
  searchQuery: string;
  sort: string;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: unknown;
  streamCounts?: StreamCounts;
  dateRange: DateRange;
  isDateFiltered: boolean;
  refetch: () => void;
  handleTabChange: (stream: SocialStreamTab) => void;
  handleSearch: (query: string) => void;
  handleSort: (newSort: string) => void;
  handleLoadMore: () => void;
  handleDownloadCsv: () => void;
  handleDateRangeChange: (dateRange: DateRange) => void;
  handleClearDateRange: () => void;
}

const SIDEBAR_COUNT_PAGE_SIZE = 1;

function useOrganicContentController({
  campaignId,
  supportsStories = false,
  supportsReels = false,
  onTabChange,
  socialStreamOverride,
  includeStreamCounts = false,
  enabled = true,
}: UseOrganicContentControllerParams): UseOrganicContentControllerResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const socialStream: SocialStreamTab = socialStreamOverride ?? 'all';

  const searchQuery = searchParams.get('q') || '';

  const start = searchParams.get('start_date');
  const end = searchParams.get('end_date');
  const dateRange: DateRange = {
    startDate:
      start && dayjs(start).isValid() ? dayjs(start).toDate() : undefined,
    endDate: end && dayjs(end).isValid() ? dayjs(end).toDate() : undefined,
  };

  const isDateFiltered = !!dateRange.startDate && !!dateRange.endDate;
  const sort = normalizePostsSort(searchParams.get('sort'), {
    dateFiltered: isDateFiltered,
  });
  const startDateParam = isDateFiltered
    ? dayjs(dateRange.startDate).format('YYYY-MM-DD')
    : undefined;
  const endDateParam = isDateFiltered
    ? dayjs(dateRange.endDate).format('YYYY-MM-DD')
    : undefined;

  const {
    posts,
    isLoading: isLoadingPosts,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchPosts,
    error: postsError,
  } = useCampaignSocialMediaPostsQuery({
    campaignId,
    socialStream,
    sort,
    q: searchQuery,
    enabled: enabled && !!campaignId,
    startDate: startDateParam,
    endDate: endDateParam,
  });

  const {
    campaignStats,
    isLoading: isLoadingMetrics,
    refetch: refetchMetrics,
    error: metricsError,
  } = useCampaignMetricsQuery({
    campaignId,
    socialStream,
    enabled: enabled && !!campaignId,
    startDate: startDateParam,
    endDate: endDateParam,
  });

  const {totalCount: allCount = 0} = useCampaignSocialMediaPostsQuery({
    campaignId,
    socialStream: 'all',
    perPage: SIDEBAR_COUNT_PAGE_SIZE,
    enabled: !!campaignId && includeStreamCounts,
  });
  const {totalCount: feedCount = 0} = useCampaignSocialMediaPostsQuery({
    campaignId,
    socialStream: 'feed',
    perPage: SIDEBAR_COUNT_PAGE_SIZE,
    enabled: !!campaignId && includeStreamCounts,
  });
  const {totalCount: storyCount = 0} = useCampaignSocialMediaPostsQuery({
    campaignId,
    socialStream: 'story',
    perPage: SIDEBAR_COUNT_PAGE_SIZE,
    enabled: !!campaignId && includeStreamCounts && supportsStories,
  });
  const {totalCount: reelsCount = 0} = useCampaignSocialMediaPostsQuery({
    campaignId,
    socialStream: 'reels',
    perPage: SIDEBAR_COUNT_PAGE_SIZE,
    enabled: !!campaignId && includeStreamCounts && supportsReels,
  });

  const isLoading = isLoadingPosts || isLoadingMetrics;
  const error = postsError || metricsError;
  const refetch = useCallback(() => {
    refetchPosts();
    refetchMetrics();
  }, [refetchPosts, refetchMetrics]);

  const handleDateRangeChange = useCallback(
    (newDateRange: DateRange) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      if (newDateRange.startDate && newDateRange.endDate) {
        nextSearchParams.set(
          'start_date',
          dayjs(newDateRange.startDate).format('YYYY-MM-DD')
        );
        nextSearchParams.set(
          'end_date',
          dayjs(newDateRange.endDate).format('YYYY-MM-DD')
        );
        const currentSort = nextSearchParams.get('sort');
        if (
          currentSort &&
          DATE_FILTER_UNSUPPORTED_SORT_FIELDS.has(postsSortField(currentSort))
        ) {
          nextSearchParams.delete('sort');
        }
      } else {
        nextSearchParams.delete('start_date');
        nextSearchParams.delete('end_date');
      }
      setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleClearDateRange = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('start_date');
    nextSearchParams.delete('end_date');
    setSearchParams(nextSearchParams);
  }, [searchParams, setSearchParams]);

  const handleTabChange = useCallback(
    (newStream: SocialStreamTab) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('q');
      nextSearchParams.delete('sort');
      // Clear date filter when switching to unsupported streams
      if (newStream !== 'feed' && newStream !== 'reels') {
        nextSearchParams.delete('start_date');
        nextSearchParams.delete('end_date');
      }
      setSearchParams(nextSearchParams);
      onTabChange?.(newStream);
    },
    [onTabChange, searchParams, setSearchParams]
  );

  const handleSearch = useCallback(
    (query: string) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      if (query) {
        nextSearchParams.set('q', query);
      } else {
        nextSearchParams.delete('q');
      }
      setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSort = useCallback(
    (newSort: string) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('sort', newSort);
      setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleDownloadCsv = useCallback(() => {
    campaignSocialMediaPostsFetchers.downloadMetricsCsv(
      campaignId,
      socialStream
    );
  }, [campaignId, socialStream]);

  return {
    posts,
    campaignStats,
    searchQuery,
    sort,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    error,
    streamCounts: includeStreamCounts
      ? {
          all: allCount,
          feed: feedCount,
          story: storyCount,
          reels: reelsCount,
        }
      : undefined,
    dateRange,
    isDateFiltered,
    refetch,
    handleTabChange,
    handleSearch,
    handleSort,
    handleLoadMore,
    handleDownloadCsv,
    handleDateRangeChange,
    handleClearDateRange,
  };
}

export default useOrganicContentController;
export type {UseOrganicContentControllerResult};
